import React, { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

type Lang = "es" | "en";

interface Settings {
  enabled: boolean;
  payment_failed_threshold: number;
  payment_window_minutes: number;
  job_failed_threshold: number;
  job_window_minutes: number;
  recipient_emails: string[];
  cooldown_minutes: number;
}

interface LogRow {
  id: string;
  alert_type: string;
  count_observed: number;
  threshold: number;
  window_minutes: number;
  recipients: string[];
  status: string;
  created_at: string;
}

const DEFAULTS: Settings = {
  enabled: true,
  payment_failed_threshold: 5,
  payment_window_minutes: 60,
  job_failed_threshold: 3,
  job_window_minutes: 60,
  recipient_emails: [],
  cooldown_minutes: 60,
};

export function AdminAlertsSettings({ lang, toast }: { lang: Lang; toast: (m: string) => void }) {
  const [s, setS] = useState<Settings>(DEFAULTS);
  const [recipientsText, setRecipientsText] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [logs, setLogs] = useState<LogRow[]>([]);

  const tr = (es: string, en: string) => (lang === "es" ? es : en);

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase.from("alert_settings").select("*").eq("id", 1).maybeSingle();
    if (data) {
      const next: Settings = {
        enabled: !!data.enabled,
        payment_failed_threshold: data.payment_failed_threshold ?? 5,
        payment_window_minutes: data.payment_window_minutes ?? 60,
        job_failed_threshold: data.job_failed_threshold ?? 3,
        job_window_minutes: data.job_window_minutes ?? 60,
        recipient_emails: Array.isArray(data.recipient_emails) ? data.recipient_emails : [],
        cooldown_minutes: data.cooldown_minutes ?? 60,
      };
      setS(next);
      setRecipientsText(next.recipient_emails.join(", "));
    }
    const { data: lg } = await supabase
      .from("alert_log")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(20);
    setLogs(lg || []);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    const recipients = recipientsText
      .split(/[,\s;]+/)
      .map((x) => x.trim().toLowerCase())
      .filter((x) => x && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(x));
    const payload = { ...s, recipient_emails: recipients, id: 1, updated_at: new Date().toISOString() };
    const { error } = await supabase.from("alert_settings").upsert(payload, { onConflict: "id" });
    setSaving(false);
    if (error) toast(tr("Error al guardar: ", "Save failed: ") + error.message);
    else {
      toast(tr("Configuración guardada", "Settings saved"));
      setS({ ...s, recipient_emails: recipients });
    }
  };

  const runNow = async (dryRun: boolean) => {
    setTesting(true);
    const { data, error } = await supabase.functions.invoke("alerts-monitor", { body: { dryRun } });
    setTesting(false);
    if (error) { toast(tr("Error al ejecutar: ", "Run failed: ") + error.message); return; }
    toast(dryRun
      ? tr("Simulación ejecutada (sin enviar email)", "Dry run executed (no email sent)")
      : tr("Monitor ejecutado", "Monitor executed"));
    console.log("[alerts-monitor]", data);
    await load();
  };

  const fmtDate = (iso: string) => new Date(iso).toLocaleString(lang === "es" ? "es-DO" : "en-US");
  const typeLabel = (t: string) =>
    t === "payment_failures" ? tr("Pagos fallidos", "Payment failures")
    : t === "job_failures" ? tr("Jobs caídos", "Job failures")
    : t;

  if (loading) return <div style={{ padding: 30, color: "var(--tx3)" }}>{tr("Cargando…", "Loading…")}</div>;

  return (
    <div className="ag">
      <div className="ag-hd">
        <h3>{tr("Alertas por email", "Email alerts")}</h3>
        <p>{tr(
          "Configura umbrales y destinatarios. El monitor corre cada 5 minutos y respeta un cooldown para evitar spam.",
          "Configure thresholds and recipients. The monitor runs every 5 minutes and respects a cooldown to avoid spam.",
        )}</p>
      </div>

      <div className="card" style={{ padding: 18, display: "grid", gap: 14 }}>
        <label style={row}>
          <input type="checkbox" checked={s.enabled} onChange={(e) => setS({ ...s, enabled: e.target.checked })} />
          <span style={{ marginLeft: 8 }}>{tr("Alertas activas", "Alerts enabled")}</span>
        </label>

        <div style={grid2}>
          <Field label={tr("Umbral pagos fallidos", "Failed payments threshold")}
            value={s.payment_failed_threshold}
            onChange={(v) => setS({ ...s, payment_failed_threshold: v })} min={1} />
          <Field label={tr("Ventana pagos (min)", "Payments window (min)")}
            value={s.payment_window_minutes}
            onChange={(v) => setS({ ...s, payment_window_minutes: v })} min={5} />
          <Field label={tr("Umbral jobs caídos", "Failed jobs threshold")}
            value={s.job_failed_threshold}
            onChange={(v) => setS({ ...s, job_failed_threshold: v })} min={1} />
          <Field label={tr("Ventana jobs (min)", "Jobs window (min)")}
            value={s.job_window_minutes}
            onChange={(v) => setS({ ...s, job_window_minutes: v })} min={5} />
          <Field label={tr("Cooldown entre alertas (min)", "Cooldown between alerts (min)")}
            value={s.cooldown_minutes}
            onChange={(v) => setS({ ...s, cooldown_minutes: v })} min={5} />
        </div>

        <div>
          <label style={{ display: "block", fontSize: 12, color: "var(--tx3)", marginBottom: 6 }}>
            {tr("Destinatarios (separados por coma)", "Recipients (comma separated)")}
          </label>
          <textarea
            value={recipientsText}
            onChange={(e) => setRecipientsText(e.target.value)}
            placeholder="admin@nutrimartrd.com, ops@nutrimartrd.com"
            rows={2}
            style={{ width: "100%", padding: 10, border: "1px solid var(--bd2)", borderRadius: 8, fontFamily: "inherit", background: "var(--bg2)", color: "var(--tx1)" }}
          />
        </div>

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button className="btn" onClick={save} disabled={saving}>
            {saving ? tr("Guardando…", "Saving…") : tr("Guardar", "Save")}
          </button>
          <button className="btn alt" onClick={() => runNow(true)} disabled={testing}>
            {testing ? "…" : tr("Simular (dry-run)", "Dry run")}
          </button>
          <button className="btn alt" onClick={() => runNow(false)} disabled={testing}>
            {testing ? "…" : tr("Ejecutar monitor ahora", "Run monitor now")}
          </button>
        </div>
      </div>

      <div className="card" style={{ padding: 18, marginTop: 14 }}>
        <h4 style={{ marginTop: 0 }}>{tr("Historial de alertas", "Alert history")}</h4>
        {logs.length === 0 ? (
          <p style={{ color: "var(--tx3)" }}>{tr("Sin alertas registradas todavía.", "No alerts logged yet.")}</p>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ textAlign: "left", color: "var(--tx3)" }}>
                  <th style={th}>{tr("Fecha", "Date")}</th>
                  <th style={th}>{tr("Tipo", "Type")}</th>
                  <th style={th}>{tr("Eventos", "Events")}</th>
                  <th style={th}>{tr("Umbral", "Threshold")}</th>
                  <th style={th}>{tr("Ventana", "Window")}</th>
                  <th style={th}>{tr("Destinatarios", "Recipients")}</th>
                  <th style={th}>{tr("Estado", "Status")}</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((l) => (
                  <tr key={l.id} style={{ borderTop: "1px solid var(--bd2)" }}>
                    <td style={td}>{fmtDate(l.created_at)}</td>
                    <td style={td}>{typeLabel(l.alert_type)}</td>
                    <td style={td}><strong>{l.count_observed}</strong></td>
                    <td style={td}>{l.threshold}</td>
                    <td style={td}>{l.window_minutes} min</td>
                    <td style={td}>{(l.recipients || []).length}</td>
                    <td style={td}>
                      <span style={{
                        background: l.status === "sent" ? "#16a34a22" : "#dc262622",
                        color: l.status === "sent" ? "#16a34a" : "#dc2626",
                        padding: "2px 8px", borderRadius: 6, fontSize: 11, fontWeight: 700,
                      }}>{l.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, min = 0 }: { label: string; value: number; onChange: (v: number) => void; min?: number }) {
  return (
    <div>
      <label style={{ display: "block", fontSize: 12, color: "var(--tx3)", marginBottom: 6 }}>{label}</label>
      <input
        type="number" min={min} value={value}
        onChange={(e) => onChange(Math.max(min, Number(e.target.value) || 0))}
        style={{ width: "100%", padding: 10, border: "1px solid var(--bd2)", borderRadius: 8, background: "var(--bg2)", color: "var(--tx1)" }}
      />
    </div>
  );
}

const row: React.CSSProperties = { display: "flex", alignItems: "center", cursor: "pointer" };
const grid2: React.CSSProperties = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12 };
const th: React.CSSProperties = { padding: "8px 6px", fontWeight: 600 };
const td: React.CSSProperties = { padding: "8px 6px" };